import { useEffect } from "react"
import { BaseTable, Card, FooterMenu, HeaderMenu, NumberInput } from "components"
import { AddReminder, Date, Deliver, Memo, PromisedDate, RecurringButton, Supplier, SupplierId, TableSection, TaxInclusive, TradingTerm } from "../components"
import { BillServiceCode, BillServiceTableForm, TabBillService } from "./components"
import { ReminderContext } from "../contexts"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import moment from "moment"
import toast from "react-hot-toast"
import { useLocation } from "react-router-dom"
import { useApi, freightAlert, convertNumber, useTaxCode } from "utils"
import { bill_service_values, DetailBillServiceValues, type BillServiceValues} from "./types"
import { PERMISION } from "security"

export const CreateBillService = () => {
  const api = useApi() 
  const location = useLocation()
  const dataTaxCode = useTaxCode()

  const defaultValues: BillServiceValues = {
    trx_code: "",
    vendor_id: "",
    vendor_name: "",
    location_id: "",
    address: "",
    memo: "Bill service transaction",
    transaction_date: moment().format("YYYY-MM-DD"),
    referensi: "",
    freight: 0,
    promise_date: moment().format("YYYY-MM-DD"),
    details: [],
    reminder: null,
  }
  const onSubmit = (value: BillServiceValues) => {
    const finalPayload = { 
      ...value, 
      vendor_name: undefined, 
      reminder: undefined,
      payment_term: undefined,
      details: value?.details?.map(val => ({
        ...val,
        tax_rate: val?.tax_rate,
        total_tax_paid: val?.total_tax_paid,
        quantity_unit: val?.mr_unit_id,
      }))
    }

    return new Promise<void>((resolve) => {
      const submitForm = (): void => {
        toast.promise(api.post("/probill/create-bill-service", finalPayload),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.detail?.message ?? err.response.data.message
          }
        )
        .then(() => {
          methods.reset()
          const overflowElement = document.querySelector('#overflow-container')
          overflowElement!.scrollTo({ top: 0, behavior: 'smooth' })
        })
        .catch(() => {})
        .finally(resolve)
      }

      if (!value.freight) {
        freightAlert("Bill Service")
        .then(() => submitForm())
        .catch(() => resolve())
      } else {
        submitForm()
      }
    })
  }
  const methods = useForm<BillServiceValues>({ defaultValues, resolver: yupResolver(bill_service_values) })

  const getSubtotalItem = (item: DetailBillServiceValues): number => item?.total!
  const detail = methods.watch("details")
  const freight = methods.watch("freight")

  const subtotal: number = detail.reduce((acc: number, item: DetailBillServiceValues): number => {
    return acc + getSubtotalItem(item)
  }, 0)
  const tax: number = detail.reduce((acc: number, item: DetailBillServiceValues): number => {
    const tax_item = dataTaxCode?.data?.find(i => i?.value === item?.tax_id)

    return acc + (getSubtotalItem(item) * (tax_item?.rate ?? 0) / 100)
  }, 0)
  const total = subtotal + freight + tax
  const totalBalance = total - 0

  useEffect(() => {
    const state = location.state

    if (state) {
      methods.setValue("vendor_id", state.vendor_id)
      methods.setValue("vendor_name", state._vendor_name)
    }

    // eslint-disable-next-line
  }, [location.state])

  return (
    <FormProvider {...methods}>
      <section className="container my-5 flex flex-col gap-2">
        <HeaderMenu title="DATA ENTRY | BILL SERVICES" className="sticky top-[30px] z-[2]" action="create">
          <BillServiceCode />
        </HeaderMenu>

        <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
          <Supplier
            name="vendor_id"
            trading_term_name="payment_term"
            vendor_name="vendor_name"
          />

          <TradingTerm name="payment_term" />
          <TaxInclusive name="tax_inclusive" isHidden />
        </section>

        <Card>
          <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <Deliver
                deliver_name="location_id"
                detail_name="address"
              />

              <Memo name="memo" />
            </section>

            <section className="flex flex-col gap-3">
              <Date name="transaction_date" />
              <SupplierId name="referensi" />
              <PromisedDate name="promize_date" />

              <ReminderContext.Provider
                value={{
                  disabled: false,
                  transaction_from: "BILL SERVICE",
                  name: {
                    memo: "memo",
                    promise_date: "promise_date",
                    reminder: "reminder",
                    referensi: "referensi",
                    transaction_date: "transaction_date",
                    vendor_name: "vendor_name",
                    reminder_child: {
                      memo: "reminder.memo",
                      promise_date: "reminder.promise_date",
                      transaction_date: "reminder.transaction_date"
                    }
                  },
                  onSuccess: (value) => {
                    methods.setValue("reminder.memo", value.memo)
                    methods.setValue("reminder.promise_date", value.promise_date)
                    methods.setValue("reminder.transaction_date", value.transaction_date)
                  }
                }}
              >
                <AddReminder />
              </ReminderContext.Provider>
            </section>
          </Card.Body>
        </Card>

        <BillServiceTableForm />

        <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <TableSection>
              <TableSection.Row label="BILL SERVICE STATUS" value="-" />
              <TableSection.Row label="APPROVAL STATUS" value="-" />
              <TableSection.Row label="APPROVED BY" value="-" />
            </TableSection>

            <RecurringButton />
          </section>
          
          <section className="flex flex-col lg:items-end gap-3">
            <BaseTable
              plain
              className="w-full min-w-[100px] md:max-w-[400px] text-right"
            >
              <tbody>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
                  <td>{convertNumber(subtotal).intoCurrency}</td>
                </tr>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">FREIGHT</td>
                  <Controller
                    control={methods.control}
                    name="freight"
                    render={({ field, fieldState }) => (
                      <NumberInput
                        inputClass="text-right"
                        error={fieldState.error?.message}
                        ref={field.ref}
                        value={field.value ?? 0}
                        onValueChange={e => {
                          const value: number = e.floatValue ?? 0
                          field.onChange(value)
                        }}
                      />
                    )}
                  />
                </tr>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">TAX</td>
                  <td>{convertNumber(tax).intoCurrency}</td>
                </tr>
                <tr className="font-bold">
                  <td className="w-[1px] whitespace-nowrap">TOTAL AMOUNT</td>
                  <td>{convertNumber(total).intoCurrency}</td>
                </tr>
                <tr className="text-error">
                  <td className="w-[1px] whitespace-nowrap">PAYMENT</td>
                  <td>{convertNumber(0).intoCurrency}</td>
                </tr>
                <tr className="border-y-2 border-black text-lg font-bold">
                  <td className="w-[1px] whitespace-nowrap">BALANCE</td>
                  <td>{convertNumber(totalBalance).intoCurrency}</td>
                </tr>
              </tbody>
            </BaseTable>
          </section>
        </section>

        <TabBillService />

        <FooterMenu
          hasCancelButton
          save={{
            permission: PERMISION.PURCHASES.TRANSACTION.CREATE_BILL_SERVICE.CREATE,
            actiontype: "save",
            type: "button",
            color: "primary",
            loading: methods.formState.isSubmitting ? "true" : undefined,
            onClick: methods.handleSubmit(onSubmit)
          }}
        />
      </section>
    </FormProvider>
  )
}